/**
 * Hosted Checkout 整页跳转回跳 URL 参数解析与清洗（PayPal / Stripe 订阅共用）。
 *
 * Safari 无痕可能剥光跨站回跳 query；下单时 return_url 会写入 sessionStorage，
 * 落地裸 /reader|/pay 后由 hosted-checkout-return-restore 同源 replace 恢复。
 */
export enum EHostedCheckoutReturnQueryKey {
    OrderId = 'order_id',
    SubscriptionId = 'subscription_id',
    CheckoutSessionId = 'checkout_session_id',
    SessionId = 'session_id',
    Option = 'option',
    BaToken = 'ba_token',
    Token = 'token',
    ReturnScene = 'return_scene',
    TriggerDramaId = 'trigger_drama_id',
}

export type THostedCheckoutReturnStatus = 'success' | 'cancel' | 'approve';

export interface IHostedCheckoutReturn {
    /** commerce order_id；cancel 时也会被后端带回 */
    orderId: string;
    /** store order id：PayPal 订阅用 subscription_id / token；Stripe 用 checkout_session_id / session_id */
    storeOrderId: string;
    /** PayPal billing agreement token */
    baToken: string;
    /** PayPal EC token */
    token: string;
    status: THostedCheckoutReturnStatus;
}

/** next/navigation ReadonlyURLSearchParams 与 URLSearchParams 的共有最小接口 */
interface IReadonlySearchParams {
    get: (name: string) => string | null;
}

function pickStoreOrderId(params: IReadonlySearchParams): string {
    const fromSubscription = params.get(EHostedCheckoutReturnQueryKey.SubscriptionId)?.trim();
    if (fromSubscription) {
        return fromSubscription;
    }
    const fromCheckoutSession = params.get(EHostedCheckoutReturnQueryKey.CheckoutSessionId)?.trim();
    if (fromCheckoutSession) {
        return fromCheckoutSession;
    }
    const fromSessionId = params.get(EHostedCheckoutReturnQueryKey.SessionId)?.trim();
    if (fromSessionId) {
        return fromSessionId;
    }
    return params.get(EHostedCheckoutReturnQueryKey.Token)?.trim() ?? '';
}

/**
 * 从 URL 查询参数解析 Hosted Checkout 回跳信息。
 * - `option` 不是 success / approve / cancel / cancelled → 返回 null（与本次跳转无关）
 * - success / approve：至少需要 `order_id`；`storeOrderId` 优先 subscription_id → checkout_session_id → session_id → token
 * - `option === 'cancel' | 'cancelled'` → 视为合法（即使其他字段缺失也允许走 cancel 处理）
 */
export function parseHostedCheckoutReturnFromSearchParams(params: IReadonlySearchParams): IHostedCheckoutReturn | null {
    const rawStatus = params.get(EHostedCheckoutReturnQueryKey.Option);
    const isCancel = rawStatus === 'cancel' || rawStatus === 'cancelled';
    const isSuccess = rawStatus === 'success' || rawStatus === 'approve';
    if (!isCancel && !isSuccess) {
        return null;
    }

    const orderId = params.get(EHostedCheckoutReturnQueryKey.OrderId) ?? '';
    const storeOrderId = pickStoreOrderId(params);
    const baToken = params.get(EHostedCheckoutReturnQueryKey.BaToken) ?? '';
    const token = params.get(EHostedCheckoutReturnQueryKey.Token) ?? '';

    if (isSuccess && !orderId) {
        return null;
    }

    if (isCancel) {
        return { orderId, storeOrderId, baToken, token, status: 'cancel' };
    }

    return {
        orderId,
        storeOrderId,
        baToken,
        token,
        status: rawStatus === 'approve' ? 'approve' : 'success',
    };
}

/** 处理完回跳之后需要从 URL 上抹掉的字段（网关回调 + 我们发起时的回声字段） */
const STRIPPED_QUERY_KEYS: readonly string[] = [
    EHostedCheckoutReturnQueryKey.OrderId,
    EHostedCheckoutReturnQueryKey.SubscriptionId,
    EHostedCheckoutReturnQueryKey.CheckoutSessionId,
    EHostedCheckoutReturnQueryKey.SessionId,
    EHostedCheckoutReturnQueryKey.Option,
    EHostedCheckoutReturnQueryKey.BaToken,
    EHostedCheckoutReturnQueryKey.Token,
    EHostedCheckoutReturnQueryKey.ReturnScene,
    EHostedCheckoutReturnQueryKey.TriggerDramaId,
    'PayerID',
];

/**
 * 返回去掉 Hosted Checkout 回跳相关 query 之后的 pathname + search + hash，
 * 供 router.replace() 调用以避免 F5 重提。
 */
export function stripHostedCheckoutReturnFromUrl(url: string): string {
    try {
        const u = new URL(url);
        STRIPPED_QUERY_KEYS.forEach(key => u.searchParams.delete(key));
        const search = u.searchParams.toString();
        return `${u.pathname}${search ? `?${search}` : ''}${u.hash ?? ''}`;
    } catch {
        return url;
    }
}
